// The landing chat's conversation, kept for this tab's session (#2809).
//
// Founder, 2026-10-01: "when I go back to the landing page, my previous
// conversation is gone." A visitor who opens a college from an answer's chip
// and comes back (the "Back to Astra" control, the browser's back button, the
// logo, a reload) finds the conversation where they left it.
//
// sessionStorage, never localStorage: it lives for this tab only and ends when
// the tab closes. It is never sent anywhere; the server already keeps no words
// (the chat's notice says so). Every read and write is wrapped: private mode,
// blocked storage or a full quota just means the conversation is not kept.
export const THREAD_KEY = 'astra_chat_thread_v1';
// The router state a chip or an in-answer link carries to the page it opens,
// so that page can offer the way back (no URL change, so the prerendered page
// and its canonical stay exactly as they are).
export const FROM_ASTRA = { from: 'astra' };
export const cameFromAstra = (state) => Boolean(state && state.from === 'astra');
// Where "Back to Astra" goes: the landing page. No #see-your-odds hash: the
// page's hash scroll would put the section's top in view, and the chat puts
// the conversation's last message there itself.
export const ASTRA_HOME = '/';

const MAX_MESSAGES = 40;
const MAX_BYTES = 200000;

// Only what the page draws: the words, the code-drawn tables, the dated
// sources and our own pages, and the small flags around them.
function clean(m) {
    if (!m || (m.role !== 'visitor' && m.role !== 'astra') || typeof m.text !== 'string') return null;
    if (m.role === 'visitor') return { role: 'visitor', text: m.text };
    const a = m.answer || {};
    return {
        role: 'astra', text: m.text, kind: m.kind === 'crisis' ? 'crisis' : 'answer',
        answer: {
            text: typeof a.text === 'string' ? a.text : m.text,
            tables: Array.isArray(a.tables) ? a.tables : [],
            sources: Array.isArray(a.sources) ? a.sources : [],
            pages: Array.isArray(a.pages) ? a.pages : [],
        },
        topic: typeof m.topic === 'string' ? m.topic : null,
        school: typeof m.school === 'string' ? m.school : null,
        nudge: Boolean(m.nudge),
        bands: Array.isArray(m.bands) ? m.bands : null,
        personalize: m.personalize && m.personalize.name ? m.personalize : null,
    };
}

export function loadChatThread() {
    try {
        const raw = sessionStorage.getItem(THREAD_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        const thread = (Array.isArray(data?.thread) ? data.thread : []).map(clean).filter(Boolean);
        if (!thread.length) return null;
        return {
            thread,
            topics: Array.isArray(data.topics) ? data.topics.filter((t) => typeof t === 'string') : [],
            total: Number.isInteger(data.total) && data.total > 0 ? data.total : null,
            nudged: data.nudged === true,
        };
    } catch {
        return null;
    }
}

// No daily limit and no questions-left here (review of #2809): a tab kept
// past midnight must not stay locked; the server says both on the next ask.
export function saveChatThread({ thread, topics, total, nudged }) {
    try {
        let kept = (thread || []).map(clean).filter(Boolean).slice(-MAX_MESSAGES);
        const pack = () => JSON.stringify({ thread: kept, topics: topics || [], total, nudged: Boolean(nudged) });
        let raw = pack();
        // too big: the oldest question and answer go first, never the newest
        while (raw.length > MAX_BYTES && kept.length > 2) {
            kept = kept.slice(2);
            raw = pack();
        }
        if (!kept.length || raw.length > MAX_BYTES) { sessionStorage.removeItem(THREAD_KEY); return; }
        sessionStorage.setItem(THREAD_KEY, raw);
    } catch { /* private mode or a full quota: the conversation lives for this view only */ }
}

export function clearChatThread() {
    try { sessionStorage.removeItem(THREAD_KEY); } catch { /* nothing kept */ }
}
